import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Types
export interface ModConfirmDeleteProps {
  open: boolean;
  onClose: () => void;
  /** ถ้าไม่ส่งมา จะไม่แสดงปุ่ม confirm (ใช้เป็น modal แจ้งเตือนอย่างเดียว) */
  onConfirm?: () => void;
  title?: string;
  /** รับได้ทั้ง string (จัดกลางให้) และ ReactNode (เช่น list ของ scene) */
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
  confirmHoverColor?: string;
}

// Styled Components
const ModalContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  backgroundColor: "white",
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  outline: "none",
});

const ModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderBottom: "1px solid #f1f5f9",
});

const ModalContent = styled(Box)({
  padding: "24px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
});

const WarningIconWrapper = styled(Box)({
  width: 56,
  height: 56,
  borderRadius: "50%",
  backgroundColor: "#fef2f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "16px",
});

const ModalFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 20px",
  borderTop: "1px solid #f1f5f9",
});

const CancelButton = styled(Button)({
  backgroundColor: "#f1f5f9",
  color: "#64748b",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "8px 20px",
  borderRadius: 8,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#e2e8f0",
    boxShadow: "none",
  },
});

const DeleteButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "white",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "8px 20px",
  borderRadius: 8,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#dc2626",
    boxShadow: "none",
  },
});

const ModConfirmDelete: React.FC<ModConfirmDeleteProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Delete",
  message = "Are you sure you want to delete this project? This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  confirmColor,
  confirmHoverColor
}) => {
  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="confirm-delete-modal-title"
    >
      <ModalContainer id="megen-mod-confirm-delete-container">
        {/* Header */}
        <ModalHeader id="megen-mod-confirm-delete-header">
          <Typography
            id="confirm-delete-modal-title"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#1e293b",
            }}
          >
            {title}
          </Typography>
          <IconButton
            id="megen-mod-confirm-delete-close-btn"
            onClick={onClose}
            size="small"
            sx={{
              color: "#94a3b8",
              padding: "4px",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent id="megen-mod-confirm-delete-content">
          <WarningIconWrapper id="megen-mod-confirm-delete-warning-icon">
            <WarningAmberIcon sx={{ fontSize: 28, color: "#ef4444" }} />
          </WarningIconWrapper>
          {typeof message === "string" ? (
            <Typography
              id="megen-mod-confirm-delete-message"
              sx={{
                fontSize: "14px",
                color: "#64748b",
                lineHeight: 1.6,
                whiteSpace: "pre-line",
              }}
            >
              {message}
            </Typography>
          ) : (
            <Box id="megen-mod-confirm-delete-message" sx={{ width: "100%" }}>
              {message}
            </Box>
          )}
        </ModalContent>

        {/* Footer */}
        <ModalFooter id="megen-mod-confirm-delete-footer">
          <CancelButton id="megen-mod-confirm-delete-cancel-btn" variant="contained" onClick={onClose}>
            {cancelText}
          </CancelButton>
          {onConfirm && (
            <Button
              id="megen-mod-confirm-delete-confirm-btn"
              variant="contained"
              onClick={handleConfirm}
              sx={{
                backgroundColor: confirmColor ?? "#ef4444",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                padding: "8px 20px",
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: confirmHoverColor ?? "#dc2626",
                  boxShadow: "none",
                },
              }}
            >
              {confirmText}
            </Button>
          )}
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default ModConfirmDelete;